<template>
  <div class="entra-page">
    <EntraPageHeader
      title="Recommendations &amp; Action Centre"
      :tenantDisplayName="tenantName"
      :primaryDomain="primaryDomain"
      :regionCode="regionCode"
      :tenantStatus="tenantStatus"
      :freshnessState="freshnessState"
      :lastCollection="lastCollection"
      :dataCompleteness="dataCompleteness"
      :capabilities="capabilities"
      :searchValue="search"
      @update:searchValue="search = $event"
      @search="applyFilters"
      @refresh="loadData"
      @export="handleExport"
    />

    <div class="page-body">
      <div class="filters-bar">
        <div class="filter-group">
          <label class="filter-label">Category:</label>
          <select v-model="filters.category" class="filter-select" @change="applyFilters">
            <option value="">All Categories</option>
            <option v-for="cat in categories" :key="cat.key" :value="cat.key">{{ cat.label }}</option>
          </select>
        </div>
        <div class="filter-group">
          <label class="filter-label">Severity:</label>
          <select v-model="filters.severity" class="filter-select" @change="applyFilters">
            <option value="">All</option>
            <option value="critical">Critical</option>
            <option value="high">High</option>
            <option value="medium">Medium</option>
            <option value="low">Low</option>
            <option value="informational">Informational</option>
          </select>
        </div>
        <div class="filter-group">
          <label class="filter-label">Status:</label>
          <select v-model="filters.status" class="filter-select" @change="applyFilters">
            <option value="">All</option>
            <option v-for="s in statusOptions" :key="s.value" :value="s.value">{{ s.label }}</option>
          </select>
        </div>
        <div class="filter-group">
          <label class="filter-label">Sort:</label>
          <select v-model="filters.sort" class="filter-select" @change="applyFilters">
            <option value="-severity">Highest Severity</option>
            <option value="-estimatedMonthlySaving">Highest Saving</option>
            <option value="-firstDetectedAt">Newest</option>
            <option value="firstDetectedAt">Oldest</option>
          </select>
        </div>
      </div>

      <div v-if="error" class="error-message">{{ error }}</div>

      <div v-if="tableState === 'loading'" class="loading-state">Loading findings...</div>
      <div v-else-if="tableState === 'empty'" class="empty-wrapper">
        <EmptyState title="No findings" description="No findings match the current filters. Great work!" />
      </div>
      <div v-else class="findings-list">
        <FindingCard
          v-for="finding in findings"
          :key="finding.id"
          :title="finding.title"
          :description="finding.description"
          :severity="finding.severity"
          :confidence="finding.confidence"
          :categoryLabel="finding.category"
          :affectedCount="1"
          :affectedLabel="finding.affectedObjectType"
          :impactText="finding.impactText"
          :evidencePreview="finding.evidencePreview"
          :firstDetected="finding.firstDetected"
          :lastEvaluated="finding.lastEvaluated"
          :ruleId="finding.ruleId"
          :ruleVersion="finding.ruleVersion"
          primaryAction="View Details"
          secondaryLabel="Actions"
          @primary-action="openFinding(finding.id)"
          @secondary-action="openActions(finding.id)"
        />
      </div>

      <div class="pagination" v-if="totalPages > 1">
        <button class="page-btn" :disabled="page <= 1" @click="goToPage(page - 1)">Previous</button>
        <span class="page-info">Page {{ page }} of {{ totalPages }}</span>
        <button class="page-btn" :disabled="page >= totalPages" @click="goToPage(page + 1)">Next</button>
      </div>

      <!-- Finding Detail Drawer -->
      <div v-if="showDrawer" class="drawer-overlay" @click.self="closeDrawer">
        <div class="drawer">
          <div class="drawer-header">
            <h2>Finding Details</h2>
            <button class="drawer-close" @click="closeDrawer">&times;</button>
          </div>
          <div class="drawer-body">
            <div v-if="selectedFinding">
              <FindingSeverityChip :severity="selectedFinding.severity" />
              <h3 style="margin-top: 12px;">{{ selectedFinding.title }}</h3>
              <p>{{ selectedFinding.description }}</p>
              <div class="detail-grid">
                <div class="detail-item"><span class="detail-label">Rule</span><span class="detail-value">{{ selectedFinding.ruleId }} v{{ selectedFinding.ruleVersion }}</span></div>
                <div class="detail-item"><span class="detail-label">Confidence</span><span class="detail-value">{{ selectedFinding.confidence }}</span></div>
                <div class="detail-item"><span class="detail-label">First Detected</span><span class="detail-value">{{ selectedFinding.firstDetected }}</span></div>
                <div class="detail-item"><span class="detail-label">Last Evaluated</span><span class="detail-value">{{ selectedFinding.lastEvaluated }}</span></div>
                <div class="detail-item"><span class="detail-label">Status</span><span class="detail-value">{{ selectedFinding.status }}</span></div>
                <div class="detail-item"><span class="detail-label">Assigned To</span><span class="detail-value">{{ selectedFinding.assignedTo || 'Unassigned' }}</span></div>
              </div>
              <div v-if="selectedFinding.evidence" class="evidence-block">
                <h4>Evidence</h4>
                <pre class="evidence-json">{{ JSON.stringify(selectedFinding.evidence, null, 2) }}</pre>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import EntraPageHeader from '@/components/entra/common/EntraPageHeader.vue';
import FindingCard from '@/components/entra/common/FindingCard.vue';
import FindingSeverityChip from '@/components/entra/common/FindingSeverityChip.vue';
import EmptyState from '@/components/entra/common/EmptyState.vue';
import { getEntraFindings, getEntraTenantSummary, getEntraTenantCapabilities } from '@/services/entra-api.js';
import { FINDING_CATEGORIES, FINDING_STATUSES, getCapabilityStateLabel } from '@/services/entra-utils.js';
import { formatCurrency } from '@/services/entra-utils.js';

export default {
  name: 'EntraRecommendations',
  components: { EntraPageHeader, FindingCard, FindingSeverityChip, EmptyState },
  data: function() {
    return {
      tenantId: '', tenantName: '', primaryDomain: '', regionCode: '', tenantStatus: '',
      freshnessState: 'fresh', lastCollection: '', dataCompleteness: null, capabilities: [],
      search: '', findings: [], tableState: 'loading',
      filters: { category: '', severity: '', status: '', sort: '-severity' },
      page: 1, totalPages: 1,
      categories: FINDING_CATEGORIES,
      statusOptions: FINDING_STATUSES.map(function(s) { return { value: s, label: s.replace(/_/g, ' ').replace(/\b\w/g, function(l) { return l.toUpperCase(); }) }; }),
      error: '',
      showDrawer: false, selectedFinding: null
    };
  },
  methods: {
    loadData: function() {
      var self = this;
      this.tenantId = this.$route.params.tenantId || '';
      if (!this.tenantId) {
        this.error = 'Please select a tenant from the Portfolio page first.';
        this.tableState = 'empty';
        return;
      }

      getEntraTenantSummary(this.tenantId).then(function(data) {
        var t = data.tenant || data;
        self.tenantName = t.displayName || '';
        self.primaryDomain = t.primaryDomain || '';
        self.regionCode = t.regionCode || '';
        self.tenantStatus = t.status || '';
        self.lastCollection = t.lastSuccessfulCollectionAt || '';
        self.dataCompleteness = t.dataCompleteness || null;
      }).catch(function() {});

      getEntraTenantCapabilities(this.tenantId).then(function(data) {
        self.capabilities = (data || []).map(function(c) {
          return { key: c.capabilityKey, state: c.state, stateLabel: getCapabilityStateLabel(c.state) };
        });
      }).catch(function() {});

      var params = { page: this.page, pageSize: 25 };
      if (this.search) params.search = this.search;
      if (this.filters.category) params.category = this.filters.category;
      if (this.filters.severity) params.severity = this.filters.severity;
      if (this.filters.status) params.status = this.filters.status;

      getEntraFindings(this.tenantId, params).then(function(data) {
        var items = (data.items || []).map(function(f) {
          return {
            id: f.findingId, title: f.title, description: f.description,
            severity: f.severity, confidence: f.confidence, category: f.category,
            affectedObjectType: f.affectedObjectType,
            impactText: f.estimatedMonthlySaving ? formatCurrency(f.estimatedMonthlySaving, f.currency) + '/mo' : '',
            evidencePreview: f.evidence ? extractEvidencePreview(f.evidence) : '',
            firstDetected: f.firstDetectedAt || '', lastEvaluated: f.lastEvaluatedAt || '',
            ruleId: f.ruleId, ruleVersion: f.ruleVersion,
            status: f.status, assignedTo: f.assignedTo, evidence: f.evidence
          };
        });
        applyClientSort(items, self.filters.sort);
        self.findings = items;
        self.totalPages = Math.ceil((data.total || 0) / 25);
        self.tableState = self.findings.length === 0 ? 'empty' : 'loaded';
      }).catch(function(err) { self.error = err.message; self.tableState = 'error'; });
    },
    applyFilters: function() { this.page = 1; this.loadData(); },
    goToPage: function(p) { this.page = p; this.loadData(); },
    handleExport: function() {},
    openFinding: function(id) {
      var f = this.findings.find(function(f) { return f.id === id; });
      if (f) { this.selectedFinding = f; this.showDrawer = true; }
    },
    openActions: function(id) { this.openFinding(id); },
    closeDrawer: function() { this.showDrawer = false; this.selectedFinding = null; }
  },
  mounted: function() { this.loadData(); },
  watch: { '$route.params.tenantId': 'loadData' }
};

function extractEvidencePreview(evidence) {
  if (!evidence) return '';
  var keys = Object.keys(evidence);
  if (keys.length === 0) return '';
  return keys.slice(0, 3).map(function(k) { return k + ': ' + JSON.stringify(evidence[k]); }).join(', ');
}

var SEVERITY_ORDER = { critical: 1, high: 2, medium: 3, low: 4, informational: 5 };

function applyClientSort(items, sortKey) {
  if (!sortKey) return;
  items.sort(function(a, b) {
    if (sortKey === '-severity') return (SEVERITY_ORDER[a.severity] || 5) - (SEVERITY_ORDER[b.severity] || 5);
    if (sortKey === '-firstDetectedAt') {
      if (!a.firstDetected) return 1;
      if (!b.firstDetected) return -1;
      return b.firstDetected.localeCompare(a.firstDetected);
    }
    if (sortKey === 'firstDetectedAt') {
      if (!a.firstDetected) return 1;
      if (!b.firstDetected) return -1;
      return a.firstDetected.localeCompare(b.firstDetected);
    }
    return 0;
  });
}
</script>

<style scoped>
.entra-page { min-height: 100%; }
.page-body { padding: 24px; }

.filters-bar { display: flex; gap: 16px; align-items: center; flex-wrap: wrap; margin-bottom: 20px; padding: 12px 16px; background: #ffffff; border: 1px solid #e2e8f0; border-radius: 8px; }
.filter-group { display: flex; align-items: center; gap: 6px; }
.filter-label { font-size: 0.75rem; font-weight: 600; color: #64748b; white-space: nowrap; }
.filter-select { padding: 4px 8px; border: 1px solid #cbd5e1; border-radius: 4px; font-size: 0.8125rem; color: #334155; background: #ffffff; font-family: 'Inter', sans-serif; }

.error-message { padding: 16px; background: #fef2f2; border: 1px solid #fecaca; border-radius: 8px; color: #991b1b; }
.loading-state { padding: 48px; text-align: center; color: #94a3b8; }

.findings-list { display: flex; flex-direction: column; gap: 12px; }

.pagination { display: flex; align-items: center; justify-content: center; gap: 12px; padding: 20px 0; }
.page-btn { background: transparent; border: 1px solid #cbd5e1; padding: 6px 14px; border-radius: 6px; font-size: 0.8125rem; color: #475569; cursor: pointer; font-family: 'Inter', sans-serif; }
.page-btn:hover:not(:disabled) { background: #f1f5f9; }
.page-btn:disabled { opacity: 0.4; cursor: not-allowed; }
.page-info { font-size: 0.8125rem; color: #64748b; }

.drawer-overlay { position: fixed; inset: 0; background: rgba(0,0,0,0.3); z-index: 2000; display: flex; justify-content: flex-end; }
.drawer { width: 600px; max-width: 90vw; background: #ffffff; height: 100%; overflow-y: auto; box-shadow: -4px 0 16px rgba(0,0,0,0.1); }
.drawer-header { display: flex; justify-content: space-between; align-items: center; padding: 20px 24px; border-bottom: 1px solid #e2e8f0; }
.drawer-header h2 { font-size: 1.125rem; font-weight: 600; color: #1e293b; margin: 0; }
.drawer-close { background: none; border: none; font-size: 1.5rem; color: #94a3b8; cursor: pointer; }
.drawer-body { padding: 24px; }

.detail-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 12px; margin: 16px 0; }
.detail-item { display: flex; flex-direction: column; gap: 2px; }
.detail-label { font-size: 0.6875rem; color: #94a3b8; font-weight: 600; text-transform: uppercase; }
.detail-value { font-size: 0.8125rem; color: #334155; }

.evidence-block { margin-top: 16px; }
.evidence-block h4 { font-size: 0.8125rem; font-weight: 600; color: #475569; margin: 0 0 8px 0; }
.evidence-json { background: #f8fafc; border: 1px solid #e2e8f0; border-radius: 4px; padding: 12px; font-size: 0.75rem; color: #334155; overflow-x: auto; white-space: pre-wrap; }
</style>
